import { z } from 'zod'

const DRAFT_KEY_PREFIX = 'rp.draft'

const StoredDraft = z.object({
  text: z.string().min(1),
  suggestionId: z.string().nullable(),
  inReplyToMessageId: z.string().nullable(),
})

export type StoredDraft = z.infer<typeof StoredDraft>

export interface DraftScope {
  userId: string
  tenantId: string
  itemId: string
}

type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>

const NO_STORAGE: DraftStorage = {
  length: 0,
  key: () => null,
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
}

export const sessionDraftStorage = (): DraftStorage => {
  try {
    return window.sessionStorage
  } catch {
    return NO_STORAGE
  }
}

const userPrefix = (userId: string): string => `${DRAFT_KEY_PREFIX}.${encodeURIComponent(userId)}.`

export const draftKey = (scope: DraftScope): string =>
  `${userPrefix(scope.userId)}${encodeURIComponent(scope.tenantId)}.${encodeURIComponent(scope.itemId)}`

const parsedDraft = (raw: string | null): StoredDraft | null => {
  if (raw === null) return null
  const parsed = StoredDraft.safeParse(JSON.parse(raw))
  return parsed.success ? parsed.data : null
}

export const readDraft = (storage: DraftStorage, scope: DraftScope): StoredDraft | null => {
  try {
    return parsedDraft(storage.getItem(draftKey(scope)))
  } catch {
    return null
  }
}

export const writeDraft = (storage: DraftStorage, scope: DraftScope, draft: StoredDraft): void => {
  try {
    storage.setItem(draftKey(scope), JSON.stringify(draft))
  } catch {
    return
  }
}

export const removeDraft = (storage: DraftStorage, scope: DraftScope): void => {
  try {
    storage.removeItem(draftKey(scope))
  } catch {
    return
  }
}

const keysOf = (storage: DraftStorage): readonly string[] =>
  Array.from({ length: storage.length }, (_unused, index) => storage.key(index)).filter(
    (key): key is string => key !== null,
  )

export const clearUserDrafts = (storage: DraftStorage, userId: string): void => {
  try {
    const prefix = userPrefix(userId)
    keysOf(storage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => storage.removeItem(key))
  } catch {
    return
  }
}
